import React, { useEffect, useState } from 'react';
import { Col } from "reactstrap";
import MobileContactModal from './ContactModal/Mobile';
import MobileChatModal from './MobileChatModal';
import logoSrc from '~/assets/images/mavsign-logo-white.svg';
import vidSrc from '~/assets/images/mobile-menu/id-verif.png';
import insuranceSrc from '~/assets/images/mobile-menu/insurance.svg';
import additionalSrc from '~/assets/images/mobile-menu/additional.svg';
import eSrc from '~/assets/images/mobile-menu/e-sign.png';
import finishSrc from '~/assets/images/mobile-menu/finish.png';
import { getOrder } from '~/helpers/backendHelper';
import { route } from '~/helpers/routeHelper';
import { useMessageChannel } from '~/hooks/messageChannel';
import { useLocation } from 'react-router-dom';
import menuSteps from '~/constants/menuSteps';
import { useAuth } from '~/context/auth';
import OrderSigner from '~/model/orderSigner';
import PresentationModal from './PresentationModal/index';
import Order from '~/model/order';

const PRESENTATION_MODAL_SEEN_KEY = 'mavsign_presentation_modal_seen';

type TMobileMenuProps = {
  activeStep: number;
}

const MobileMenu = ({ activeStep }: TMobileMenuProps) => {

  const { pathname } = useLocation();
  const { user: authUser } = useAuth();

  /********** STATE **********/

  // extract the channel from url params
  // this is useful when the user receives an SMS with the URL for a specific message channel, for which we need to open the chat modal
  const routeChannel = useMessageChannel();
  const [channel, setChannel] = useState(routeChannel);

  // in case of redirect to chat
  // wait until we are redirected from `/messages` to `/` to open the chat
  const shouldOpenChat = !!channel && pathname !== route("messages");

  const [order, setOrder] = useState<Order | null>(null);

  const [chatModal, showChatModal] = useState(shouldOpenChat);
  const [menu, setMenu] = useState([]);
  const [contactModal, showContactModal] = useState(false);
  const [presentationModal, showPresentationModal] = useState(false);

  /********** OTHER **********/

  // Calculate current step and total steps for progress display
  const getCurrentStepInfo = () => {
    if (!menu.length) return { currentStep: 0, totalSteps: 0, currentStepName: '' };

    // Find current step index based on activeStep
    let currentStepIndex = menu.findIndex((item: any) => item.step === activeStep);

    // Special handling for signature steps
    if (currentStepIndex === -1 && (activeStep === menuSteps.ESIGNATURES || activeStep === menuSteps.INKSIGNATURES)) {
      // Look for the generic SIGNATURES step in menu
      currentStepIndex = menu.findIndex((item: any) => item.step === menuSteps.SIGNATURES);
    }

    const currentStep = currentStepIndex >= 0 ? currentStepIndex + 1 : 1;
    const currentStepName = currentStepIndex >= 0 ? (menu[currentStepIndex] as any).label : (menu[0] as any)?.label || '';

    return {
      currentStep,
      totalSteps: menu.length,
      currentStepName
    };
  };

  const { currentStep, totalSteps, currentStepName } = getCurrentStepInfo();

  /********** EFFECTS **********/

  // runs once on component mount
  useEffect(() => {
    getOrder().then(res => {
      let localMenu = [];
      // if there is no order, the menu doesn't have any items
      if (!res.order) {
        return;
      }
      setOrder(res.order);
      // after the data is loaded, we build the steps menu according to the services added to the order (VID, ink-sign, e-sign)
      // localMenu is a list of objects with the following properties:
      // - label -> name of the step
      // - imgSrc -> representative icon of the step
      // - step -> an unique step number that helps us display the user progress
      // (all steps lower than or equal to the active step are marked as completed in the menu UI)
      if (res.order.customerLocation === Order.CUSTOMER_LOCATION_IN_STORE) {
        localMenu.push({
          label: "Insurance",
          imgSrc: insuranceSrc,
          step: menuSteps.INSURANCE,
        });
      } else {
        if (res.order.isVidRequired) {
          localMenu.push({
            label: "ID Verification",
            imgSrc: vidSrc,
            step: menuSteps.VID,
          });
        }
        if (res.order.isInsuranceVerificationRequired) {
          localMenu.push({
            label: "Insurance",
            imgSrc: insuranceSrc,
            step: menuSteps.INSURANCE,
          });
        }
        if (authUser?.position === OrderSigner.POSITION_MAIN_SIGNER) {
          if (res.order.isInsuranceVerificationRequired) {
            if (res.order.isGuaranteeRequired && !authUser?.hasInsurance && (res.order.secondFormOfId !== "" || res.order.secondFormOfId !== null) || !!res.order.secondFormOfId) {
              localMenu.push({
                label: "Additional Info",
                imgSrc: additionalSrc,
                step: menuSteps.ADDITIONALINFORMATION,
              });
            }
          } else {
            if ((res.order.isGuaranteeRequired && (res.order.secondFormOfId !== "" || res.order.secondFormOfId !== null)) || !!res.order.secondFormOfId) {
              localMenu.push({
                label: "Additional Info",
                imgSrc: additionalSrc,
                step: menuSteps.ADDITIONALINFORMATION,
              });
            }
          }
        }
        if (res.order.isEsignRequired) {
          localMenu.push({
            label: res.order.isEsignRequired && res.order.isInkSignRequired ? "Signatures" : "E-Signatures",
            imgSrc: eSrc,
            step: menuSteps.ESIGNATURES,
          });
        }
        if (res.order.isInkSignRequired) {
          localMenu.push({
            label: res.order.isEsignRequired && res.order.isInkSignRequired ? "Signatures" : "Ink Signatures",
            imgSrc: eSrc,
            step: menuSteps.INKSIGNATURES,
          });
        }
      }
      // no matter the type of order, the menu should always contain a "Finish" step
      localMenu.push({
        label: "Finish",
        imgSrc: finishSrc,
        step: menuSteps.FINISH,
      });
      setMenu(localMenu as any);
    })
  }, []);

  // show presentation modal only the first time customer enters the app (persisted in localStorage)
  useEffect(() => {
    if (!order) return;
    if (authUser?.type === OrderSigner.TYPE_CUSTOMER && !localStorage.getItem(PRESENTATION_MODAL_SEEN_KEY) && order?.customerLocation !== Order.CUSTOMER_LOCATION_IN_STORE) {
      showPresentationModal(true);
      localStorage.setItem(PRESENTATION_MODAL_SEEN_KEY, '1');
    }
  }, [order]);

  /********** HANDLERS **********/

  const toggleContactModal = () => {
    showContactModal(current => !current);
  };

  const toggleChatModal = () => {
    showChatModal(current => !current);
  };

  const openChatModal = (channel: any) => {
    setChannel(channel)
    showContactModal(false);
    showChatModal(true);
  };

  const togglePresentationModal = () => {
    showPresentationModal(current => !current);
  };

  return <React.Fragment>
    <div id="mobileMenu">
      <Col xs={12} md={9} lg={6} xl={5}>
        <div id="topMenuWrapper">
          <div id="header">
            <div className="header-logo">
              <img src={logoSrc} />
            </div>
            {/* hide contact modal for dealer users, as they shouldn't have access to the customer's ways of communication */}
            {authUser?.type !== OrderSigner.TYPE_DEALER && order?.customerLocation !== Order.CUSTOMER_LOCATION_IN_STORE && <div className="header-btn-wrapper">
              <button className="header-btn pe-3" onClick={togglePresentationModal}>
                {`What's next?`}
              </button>
              <button className="header-btn" onClick={toggleContactModal}>
                Contact us
              </button>
            </div>}
          </div>

          {/* Progress Section */}
          {currentStepName && (
            <div className="progress-section">
              <div className="progress-header">
                <span className="progress-step-name">{currentStepName}</span>
                <span className="progress-counter"><strong>Step {currentStep}/{totalSteps}</strong></span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </Col>
    </div>
    {order && <MobileContactModal order={order} open={contactModal} onToggle={toggleContactModal} onOpenChat={openChatModal} />}
    <MobileChatModal open={chatModal} onToggle={toggleChatModal} channel={channel} />
    <PresentationModal open={presentationModal} onToggle={togglePresentationModal} />
  </React.Fragment>
}

export default MobileMenu;